import { appendBadge as appendRoutineBadge } from '../ui/routines.js';
/**
 * View Orchestrator
 * Handles view switching, rendering, read-only badge, and sidebar tree rendering.
 * Extracted from main.js for better separation of concerns.
 */

import { normalizeViewName } from "../../../engine/core/view-names.js";
import * as MultiSelect from "../ui/multi-select.js";
import { isMobileShellVisible } from "../../../shared/utils/device-detect.js";
import { t } from "../../../shared/i18n/index.js";
import { shortcutHintsHtml } from "../views/shortcut-hints.js";

// Stored callbacks
let cb = null;
let centerRequest = 0;

/** Select from a list, then pan the active view after its selection render. */
export async function selectAndCenterNode(nodeId) {
  if (!cb?.Store.find(nodeId)) return false;
  const request = ++centerRequest;
  const {currentDocId, currentView} = cb.Store.getState();
  cb.Store.setSelected(nodeId);
  if (isMobileShellVisible()) return false;
  // Store subscribers schedule view updates, which in turn schedule layout.
  await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  const stillCurrent = () => {
    const current = cb.Store.getState();
    return request === centerRequest && current.currentDocId === currentDocId
      && current.currentView === currentView && current.selectedId === nodeId && !isMobileShellVisible();
  };
  const view = cb.views[currentView]?.module;
  // A newly opened bubble layout can still be settling. Center its final
  // position, and abandon the request if the user navigates elsewhere.
  while (stillCurrent() && view?.isLayoutPending?.()) {
    await new Promise(resolve => requestAnimationFrame(resolve));
  }
  if (!stillCurrent()) return null;
  return view?.centerOnNode?.(nodeId) || false;
}

// Cache for renderTree to avoid unnecessary full rebuilds
let _lastTreeData = null;
let _lastTreeFocusId = null;
let _lastTreeSelectedId = null;
let _lastTreeHideCompleted = null;
let _lastTreeHiddenTagIds = null;
let _lastTreeHighlightedTagIds = null;
// Sidebar rows kept between renders (id → { item, row, children, sig, pass }),
// so a change redraws only the rows it changed. Redrawing every row took
// about 0.8 s per edit at 20,000 items, nearly all of it emptying the tree
// and laying it out again (docs/internal/perf/2026-10-08-big-maps.md).
let _treeRows = new Map();
let _treeRowsPass = 0;

/**
 * Initialize view orchestrator with callbacks.
 *
 * @param {Object} callbacks - Callbacks object with dependencies:
 *   - Store: { getState, get, find, findParent, setView, setSelected, setFocus, isReadOnly }
 *   - views: { [name]: { module, container } }
 *   - sharedCallbacks: object passed to view.render()
 *   - hideNotesOverlay: () => void
 *   - updateNode: (id, updates) => boolean
 *   - moveNode: (id, newParentId, index) => void
 */
export function setupViewOrchestrator(callbacks) {
  cb = callbacks;
  setupTreeSpacingControls();
  setupTreeDragDrop();
  setupSidebarGapEvents();
}

/**
 * Forward wheel/click events from sidebar gap area to the tree,
 * so scrolling and clicking over the panel margins still works.
 */
function setupSidebarGapEvents() {
  const sidebar = document.getElementById('sidebar');
  const treeEl = document.getElementById('tree');
  if (!sidebar || !treeEl) return;

  sidebar.addEventListener('wheel', (e) => {
    // Only forward if the wheel event is on the gap (sidebar itself), not inside the panel
    const panel = sidebar.querySelector('.sidebar-panel');
    if (panel && !panel.contains(e.target)) {
      treeEl.scrollTop += e.deltaY;
      e.preventDefault();
    }
  }, { passive: false });
}

/**
 * Setup tree spacing +/- controls with localStorage persistence.
 */
function setupTreeSpacingControls() {
  const DEFAULT_SPACING = 8;
  const MIN_SPACING = 2;
  const MAX_SPACING = 16;
  const STEP = 2;

  const treeEl = document.getElementById('tree');
  const btnIn = document.getElementById('tree-spacing-in');
  const btnOut = document.getElementById('tree-spacing-out');
  const lvl = document.getElementById('tree-spacing-lvl');
  if (!treeEl || !btnIn || !btnOut) return;

  let spacing = parseInt(localStorage.getItem('tree-spacing')) || DEFAULT_SPACING;
  apply();

  btnIn.addEventListener('click', () => {
    spacing = Math.min(MAX_SPACING, spacing + STEP);
    apply();
  });
  btnOut.addEventListener('click', () => {
    spacing = Math.max(MIN_SPACING, spacing - STEP);
    apply();
  });

  function apply() {
    treeEl.style.setProperty('--tree-v-pad', spacing + 'px');
    localStorage.setItem('tree-spacing', spacing);
    if (lvl) lvl.textContent = Math.round((spacing / DEFAULT_SPACING) * 100) + '%';
  }
}

/**
 * Depth-aware drag-and-drop for sidebar tree.
 * Uses pointer events (not HTML5 DnD) for full control over visuals.
 * Event delegation on #tree survives renderTree() rebuilds.
 */
function setupTreeDragDrop() {
  const treeEl = document.getElementById('tree');
  if (!treeEl) return;

  // Read indent from CSS variable (single source of truth: --tree-indent on .tree)
  const DEPTH_INDENT = parseInt(getComputedStyle(treeEl).getPropertyValue('--tree-indent')) || 38;
  // px from #tree padding-box edge to depth-0 dot center (16 tree-pad-left + 8 node-pad + 5 half-dot)
  const DOT_OFFSET = 29;
  const DRAG_THRESHOLD = 4; // px before drag actually starts

  const COLOR_PALETTE = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
    '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
    '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e',
  ];

  let draggedNodeId = null;
  let ghost = null;
  let dropIndicator = null;
  let dropGuides = null;
  let lastGuideKey = '';
  let dropTarget = null;
  let highlightedParent = null;
  let lastHighlightParentId = null;
  let startX = 0, startY = 0;
  let dragStarted = false;

  function getDescendantIds(nodeId) {
    const ids = [];
    const node = cb.Store.find(nodeId);
    if (!node) return ids;
    (function walk(n) {
      if (n.children) {
        for (const child of n.children) { ids.push(child.id); walk(child); }
      }
    })(node);
    return ids;
  }

  function isDescendantOf(ancestorId, nodeId) {
    let current = cb.Store.findParent(nodeId);
    while (current) {
      if (current.id === ancestorId) return true;
      current = cb.Store.findParent(current.id);
    }
    return false;
  }

  function getNewColor(existingColors) {
    for (const color of COLOR_PALETTE) {
      if (!existingColors.includes(color)) return color;
    }
    return COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)];
  }

  function applyColorToSubtree(nodeId, color) {
    cb.updateNode(nodeId, { color });
    const node = cb.Store.find(nodeId);
    if (node?.children) {
      for (const child of node.children) {
        applyColorToSubtree(child.id, color);
      }
    }
  }

  /** Get all visible .tree-node elements not in the dragged subtree. */
  function getVisibleNodes() {
    return Array.from(treeEl.querySelectorAll('.tree-node:not(.dragging)'));
  }

  // Row-rect snapshot for the drag. Rows don't move during a drag, so the
  // per-pointermove getBoundingClientRect over every visible row (thousands
  // of forced-layout reads per second, interleaved with the indicator style
  // writes) is replaced by one capture in CONTENT coordinates (scroll-
  // invariant: contentY = clientY - treeRect.top + scrollTop).
  let dragRowRects = null;
  let dragTreeRect = null;

  function captureDragRects() {
    dragTreeRect = treeEl.getBoundingClientRect();
    const scrollTop = treeEl.scrollTop;
    dragRowRects = getVisibleNodes().map((el) => {
      const r = el.getBoundingClientRect();
      return {
        el,
        contentTop: r.top - dragTreeRect.top + scrollTop,
        contentMid: r.top + r.height / 2 - dragTreeRect.top + scrollTop,
        contentBottom: r.bottom - dragTreeRect.top + scrollTop,
      };
    });
  }

  // ---- pointerdown (delegated) ----
  treeEl.addEventListener('pointerdown', (e) => {
    const handle = e.target.closest('.tree-drag-handle');
    if (!handle) return;
    const nodeDiv = handle.closest('.tree-node');
    if (!nodeDiv) return;

    const nodeId = nodeDiv.dataset.id;
    const state = cb.Store.getState();
    if (nodeId === state.data?.id) return; // can't drag root

    e.preventDefault();
    draggedNodeId = nodeId;
    startX = e.clientX;
    startY = e.clientY;
    dragStarted = false;

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  });

  function beginDrag(e) {
    dragStarted = true;
    const nodeDiv = treeEl.querySelector(`.tree-node[data-id="${draggedNodeId}"]`);
    if (!nodeDiv) return;

    // Mark dragged node + descendants
    const treeItem = nodeDiv.closest('.tree-item');
    if (treeItem) treeItem.classList.add('dragging');
    nodeDiv.classList.add('dragging');
    for (const id of getDescendantIds(draggedNodeId)) {
      const el = treeEl.querySelector(`.tree-node[data-id="${id}"]`);
      if (el) {
        el.classList.add('dragging');
        const item = el.closest('.tree-item');
        if (item) item.classList.add('dragging');
      }
    }

    // Create ghost that follows pointer
    ghost = document.createElement('div');
    ghost.style.cssText = 'position:fixed;pointer-events:none;z-index:10000;padding:6px 12px;background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:6px;font-size:12px;color:var(--text-primary);white-space:nowrap;opacity:0.9;';
    ghost.textContent = cb.Store.find(draggedNodeId)?.name || t('views.node');
    document.body.appendChild(ghost);
    ghost.style.left = `${e.clientX + 12}px`;
    ghost.style.top = `${e.clientY - 12}px`;
    document.body.style.cursor = 'grabbing';
    // Esc cancels the drag (and isn't taken by the selection or "go up").
    document.body.classList.add('dragging-node');
    document.addEventListener('keydown', onTreeKey);
  }

  function onTreeKey(e) {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    cleanup();
  }

  // ---- pointermove ----
  function onPointerMove(e) {
    if (!draggedNodeId) return;

    // Wait for threshold before starting visual drag
    if (!dragStarted) {
      const dx = e.clientX - startX, dy = e.clientY - startY;
      if (dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return;
      beginDrag(e);
    }

    // Move ghost
    ghost.style.left = `${e.clientX + 12}px`;
    ghost.style.top = `${e.clientY - 12}px`;

    // (Re)capture row rects: first move after beginDrag, or after a remote
    // store update re-rendered the tree mid-drag (detached cached elements).
    if (!dragRowRects || (dragRowRects.length > 0 && !dragRowRects[0].el.isConnected)) {
      captureDragRects();
    }
    if (dragRowRects.length === 0) return;

    // Find above/below rows at cursor Y, in scroll-invariant content coords
    const contentY = e.clientY - dragTreeRect.top + treeEl.scrollTop;
    let aboveRow = null;
    let belowRow = null;
    for (const row of dragRowRects) {
      if (contentY >= row.contentMid) {
        aboveRow = row;
      } else {
        belowRow = row;
        break;
      }
    }
    const aboveNode = aboveRow?.el || null;
    const belowNode = belowRow?.el || null;

    // Depth range
    const aboveDepth = aboveNode ? parseInt(aboveNode.dataset.depth) || 0 : -1;
    const belowDepth = belowNode ? parseInt(belowNode.dataset.depth) || 0 : 0;
    const maxDepth = aboveNode ? aboveDepth + 1 : 0;
    const minDepth = (belowNode && belowDepth > aboveDepth) ? aboveDepth + 1 : 0;

    // Map cursor X to target depth
    const relativeX = e.clientX - dragTreeRect.left;
    let targetDepth = Math.floor((relativeX - DOT_OFFSET) / DEPTH_INDENT);
    targetDepth = Math.max(minDepth, Math.min(targetDepth, maxDepth));

    // Determine parent and insert index
    const state = cb.Store.getState();
    const rootId = state.data?.id;
    let parentId = null;
    let insertIndex = 0;

    if (aboveNode) {
      const aboveNodeId = aboveNode.dataset.id;
      if (targetDepth === aboveDepth + 1) {
        parentId = aboveNodeId;
        if (belowNode) {
          const belowNodeId = belowNode.dataset.id;
          const belowParent = cb.Store.findParent(belowNodeId);
          if (belowParent?.id === aboveNodeId) {
            const siblings = belowParent.children || [];
            const idx = siblings.findIndex(c => c.id === belowNodeId);
            insertIndex = idx >= 0 ? idx : siblings.length;
          } else {
            insertIndex = (cb.Store.find(aboveNodeId)?.children || []).length;
          }
        } else {
          insertIndex = (cb.Store.find(aboveNodeId)?.children || []).length;
        }
      } else {
        let currentId = aboveNodeId;
        for (let d = aboveDepth; d >= targetDepth; d--) {
          const parent = cb.Store.findParent(currentId);
          if (!parent) break;
          insertIndex = (parent.children || []).findIndex(c => c.id === currentId) + 1;
          currentId = parent.id;
        }
        parentId = currentId;
      }
    } else {
      parentId = rootId;
      insertIndex = 0;
    }

    dropTarget = { parentId, insertIndex };

    // ---- Show drop indicator ----
    if (!dropIndicator) {
      dropIndicator = document.createElement('div');
      dropIndicator.className = 'tree-drop-indicator';
      treeEl.appendChild(dropIndicator);
    }

    let indicatorTop;
    if (aboveRow) {
      indicatorTop = aboveRow.contentBottom + 1;
    } else if (belowRow) {
      indicatorTop = belowRow.contentTop - 1;
    } else {
      indicatorTop = 0;
    }

    const indent = DOT_OFFSET + targetDepth * DEPTH_INDENT;
    dropIndicator.style.left = `${indent}px`;
    dropIndicator.style.top = `${indicatorTop}px`;

    // ---- Show depth guides ----
    if (!dropGuides) {
      dropGuides = document.createElement('div');
      dropGuides.className = 'tree-drop-guides';
      treeEl.appendChild(dropGuides);
    }
    dropGuides.style.top = `${indicatorTop}px`;

    const guideKey = `${minDepth}-${maxDepth}`;
    if (guideKey !== lastGuideKey) {
      lastGuideKey = guideKey;
      dropGuides.innerHTML = '';

      if (maxDepth > minDepth) {
        const line = document.createElement('div');
        line.className = 'tree-drop-guide-line';
        line.style.left = `${DOT_OFFSET + minDepth * DEPTH_INDENT}px`;
        line.style.width = `${(maxDepth - minDepth) * DEPTH_INDENT}px`;
        dropGuides.appendChild(line);
      }

      for (let d = minDepth; d <= maxDepth; d++) {
        const dot = document.createElement('div');
        dot.className = 'tree-drop-guide-dot';
        dot.style.left = `${DOT_OFFSET + d * DEPTH_INDENT}px`;
        dropGuides.appendChild(dot);
      }
    }

    // ---- Highlight target parent (only update on change) ----
    if (parentId !== lastHighlightParentId) {
      if (highlightedParent) {
        highlightedParent.classList.remove('drop-parent');
        highlightedParent = null;
      }
      lastHighlightParentId = parentId;
      if (parentId) {
        const parentEl = treeEl.querySelector(`.tree-node[data-id="${parentId}"]`);
        if (parentEl) {
          parentEl.classList.add('drop-parent');
          highlightedParent = parentEl;
        }
      }
    }
  }

  // ---- pointerup ----
  function onPointerUp(e) {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);

    if (!draggedNodeId || !dragStarted) { cleanup(); return; }
    if (!dropTarget) { cleanup(); return; }

    const { parentId, insertIndex } = dropTarget;
    if (!parentId || parentId === draggedNodeId) { cleanup(); return; }
    if (isDescendantOf(draggedNodeId, parentId)) { cleanup(); return; }

    // A row of the multi-selection moves them all, then each moved branch
    // takes its new colour like a single move.
    if (MultiSelect.movesSelection(draggedNodeId)) {
      const moving = MultiSelect.topLevelIds((id) => cb.Store.findParent(id));
      const { movedIds } = MultiSelect.moveSelectionTo(parentId, insertIndex);
      if (movedIds.length) {
        const rootNode = cb.Store.getState().data;
        const used = (rootNode.children || []).filter(c => !moving.includes(c.id)).map(c => c.color);
        cb.beginBatch?.();
        try {
          for (const id of moving.filter((m) => movedIds.includes(m))) {
            const color = parentId === rootNode.id ? getNewColor(used) : cb.Store.find(parentId)?.color;
            if (!color) continue;
            used.push(color);
            applyColorToSubtree(id, color);
          }
        } finally {
          cb.endBatch?.();
        }
      }
      cleanup();
      return;
    }

    // Adjust index if moving within same parent
    let idx = insertIndex;
    const dragParent = cb.Store.findParent(draggedNodeId);
    if (dragParent?.id === parentId) {
      const dragIndex = (dragParent.children || []).findIndex(c => c.id === draggedNodeId);
      if (dragIndex >= 0 && dragIndex < idx) idx--;
    }

    // Move the node
    cb.moveNode(draggedNodeId, parentId, idx);

    // Handle color: root children get unique color, others inherit parent's
    const state = cb.Store.getState();
    const rootId = state.data?.id;
    if (parentId === rootId) {
      const rootNode = state.data;
      const existingColors = (rootNode.children || [])
        .filter(c => c.id !== draggedNodeId)
        .map(c => c.color);
      const newColor = getNewColor(existingColors);
      applyColorToSubtree(draggedNodeId, newColor);
    } else {
      const parentNode = cb.Store.find(parentId);
      if (parentNode?.color) {
        applyColorToSubtree(draggedNodeId, parentNode.color);
      }
    }

    cleanup();
  }

  function removeDropVisuals() {
    if (dropIndicator) { dropIndicator.remove(); dropIndicator = null; }
    if (dropGuides) { dropGuides.remove(); dropGuides = null; lastGuideKey = ''; }
    if (highlightedParent) { highlightedParent.classList.remove('drop-parent'); highlightedParent = null; }
    lastHighlightParentId = null;
  }

  function cleanup() {
    removeDropVisuals();
    if (ghost) { ghost.remove(); ghost = null; }
    document.body.style.cursor = '';
    treeEl.querySelectorAll('.dragging').forEach(el => el.classList.remove('dragging'));
    draggedNodeId = null;
    dropTarget = null;
    dragStarted = false;
    dragRowRects = null;
    dragTreeRect = null;
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('keydown', onTreeKey);
    document.body.classList.remove('dragging-node');
  }
}

/**
 * Switch to the named view and render it.
 */
export function switchView(viewName) {
  viewName = normalizeViewName(viewName);
  // Reset tree cache on view switch to ensure fresh render
  resetTreeCache();

  const views = cb.views;

  // Destroy the currently active view before switching
  const activeViewEl = document.querySelector(".view.active");
  if (activeViewEl) {
    const activeViewId = activeViewEl.id.replace("-view", "");
    const activeViewConfig = views[activeViewId];
    if (activeViewConfig?.module?.destroy) {
      activeViewConfig.module.destroy();
    }
  }

  // Update active tab
  document.querySelectorAll(".tab").forEach((tab) => {
    tab.classList.toggle("active", tab.dataset.view === viewName);
  });

  // Update active view container
  document.querySelectorAll(".view").forEach((viewEl) => {
    viewEl.classList.toggle("active", viewEl.id === `${viewName}-view`);
  });

  // Hide notes panel when in notes view (it would be redundant)
  if (viewName === "notes") {
    cb.hideNotesOverlay({ preservePreference: true });
  }

  const viewConfig = views[viewName];

  // Lazy views (notes, markdown): the module arrives with the editor-stack
  // chunk on first activation. Kick the load and re-run switchView once it
  // lands — but only if the user is still on this view by then.
  if (viewConfig && !viewConfig.module && viewConfig.load) {
    if (viewConfig.container) {
      viewConfig.container.innerHTML =
        `<p style="padding:2rem;color:var(--text-secondary)">${t('common.loading')}</p>`;
    }
    if (!viewConfig._loading) {
      viewConfig._loading = viewConfig.load()
        .then((m) => {
          viewConfig.module = m;
          viewConfig._loading = null;
          if (cb.Store.get("currentView") === viewName) {
            switchView(viewName);
          }
        })
        .catch((err) => {
          viewConfig._loading = null;
          console.error(`[ViewOrchestrator] Failed to load view "${viewName}":`, err);
          if (viewConfig.container) {
            viewConfig.container.innerHTML =
              `<p style="padding:2rem;color:var(--text-secondary)">${t('views.loadFailed')}</p>`;
          }
        });
    }
  }

  // Render the view — unless the mobile shell is on screen, where the
  // stylesheet hides #app entirely. Same reasoning as the renderTree gate
  // below: on a large document this render dominates doc-open time, for DOM
  // nobody can see. The mobile map tabs render on demand through
  // renderDesktopView when they mount the view, and the media-flip listener
  // in main.js re-runs switchView when the desktop UI comes back.
  if (viewConfig && viewConfig.module?.render && viewConfig.container && !isMobileShellVisible()) {
    const state = cb.Store.getState();
    if (!state.data) {
      console.warn('[ViewOrchestrator] No data loaded yet, skipping render for:', viewName);
      viewConfig.container.innerHTML = `<p style="padding:2rem;color:var(--text-secondary)">${t('views.loadingDots')}</p>`;
      return;
    }
    viewConfig.module.render(viewConfig.container, state, cb.sharedCallbacks);
  }

  // Update store (may be no-op if view unchanged, e.g., on refresh)
  cb.Store.setView(viewName);

  // Always render tree - Store.setView is a no-op when view unchanged,
  // so subscriber won't fire. We need to ensure tree is rendered.
  renderTree();

  // Update hints based on viewer role
  updateViewerHints();
}

/**
 * Update shortcut hints based on whether user is a viewer (read-only)
 */
export function updateViewerHints() {
  const state = cb.Store.getState();
  const isViewer = state.isShared && state.shareRole === "viewer";

  const viewerHint = t('views.hints.viewerMode');

  // Normal hints for each view
  const normalHints = shortcutHintsHtml();

  // Update each hint element
  for (const [view, normalText] of Object.entries(normalHints)) {
    const hintEl = document.getElementById(`${view}-hint`);
    if (hintEl) {
      if (isViewer) {
        hintEl.textContent = viewerHint;
      } else {
        hintEl.innerHTML = normalText;
      }
    }
  }

  // Also update the bubble view hint (it's dynamically created)
  const bubbleHint = document.getElementById("b-hint");
  if (bubbleHint) {
    if (isViewer) {
      bubbleHint.textContent = viewerHint;
    } else {
      bubbleHint.innerHTML = normalHints.bubble;
    }
  }

  // Add/remove read-only class on body for CSS-based disabling
  document.body.classList.toggle("read-only-mode", isViewer);

  // Add/remove read-only badge in header
  updateReadOnlyBadge(isViewer);
}

/**
 * Update the read-only badge in the header
 */
export function updateReadOnlyBadge(isViewer) {
  const existingBadge = document.getElementById("read-only-badge");

  if (isViewer) {
    if (!existingBadge) {
      const badge = document.createElement("span");
      badge.id = "read-only-badge";
      badge.className = "read-only-badge";
      badge.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        ${t('views.viewOnly')}
      `;
      // Insert after the doc title
      const docTitle = document.getElementById("doc-title-text");
      if (docTitle && docTitle.parentNode) {
        docTitle.parentNode.insertBefore(badge, docTitle.nextSibling);
      }
    }
  } else {
    if (existingBadge) {
      existingBadge.remove();
    }
  }
}

/**
 * Reset tree cache (call on document switch or view switch): the next render
 * compares every kept row with the tree. `rows: true` also drops the rows, so
 * it draws every row again (check-sidebar-tree compares the two).
 */
export function resetTreeCache({ rows = false } = {}) {
  if (rows) {
    _treeRows = new Map();
    document.getElementById("tree")?.replaceChildren();
  }
  // Only the data: the focus and filters stay as drawn, so a view switch is
  // not taken for a mass change (which restyles every row).
  _lastTreeData = null;
}

/**
 * Render the sidebar tree with collapsible nodes and share badges.
 */
/**
 * Delegated click/dblclick handling for the sidebar tree. Attached once to
 * the persistent #tree element; survives every innerHTML rebuild. Replaces
 * the previous 3-listeners-per-row pattern (select, focus, collapse-toggle).
 */
function _attachTreeListeners(treeEl) {
  if (treeEl.dataset.delegatedListeners) return;
  treeEl.dataset.delegatedListeners = "true";

  treeEl.addEventListener("click", (e) => {
    const toggle = e.target.closest(".tree-collapse-toggle");
    if (toggle) {
      e.stopPropagation();
      const id = toggle.closest(".tree-node")?.dataset.id;
      if (id) {
        const node = cb.Store.find(id);
        if (node) cb.updateNode(id, { collapsed: !node.collapsed });
      }
      return;
    }
    const nodeDiv = e.target.closest(".tree-node");
    if (nodeDiv?.dataset.id) {
      e.stopPropagation();
      selectAndCenterNode(nodeDiv.dataset.id);
    }
  });

  treeEl.addEventListener("dblclick", (e) => {
    const nodeDiv = e.target.closest(".tree-node");
    if (nodeDiv?.dataset.id) {
      e.stopPropagation();
      cb.Store.setFocus(nodeDiv.dataset.id);
    }
  });
}

export function renderTree() {
  const treeEl = document.getElementById("tree");
  if (!treeEl) {
    console.log("Tree element not found");
    return;
  }

  // #tree lives inside <aside id="sidebar">, and while the mobile shell is on
  // screen the stylesheet hides both the sidebar and .main outright. Rebuilding
  // it there is work nobody can see, and on a large document it dominates the
  // frame: profiling an 890-node doc on a throttled phone put a third of every
  // edit in this function.
  //
  // isMobileShellVisible() checks the media query as well as the body class,
  // because the class alone is also set on a wide desktop window (the shell is
  // initialised unconditionally and simply stays hidden there) — keying off it
  // by itself stops the desktop sidebar rendering at all.
  //
  // Returning before the cache assignments below is deliberate: the _lastTree*
  // values stay stale, so the first call after the desktop UI comes back
  // (rotate, resize, shell teardown) still does a full rebuild rather than
  // believing it is already up to date.
  if (isMobileShellVisible()) {
    return;
  }

  _attachTreeListeners(treeEl);

  const state = cb.Store.getState();

  const currentHiddenTagIds = state.hiddenTagIds;
  const currentHighlightedTagIds = state.highlightedTagIds;

  // Fast path: if only selectedId changed, just toggle CSS classes
  if (
    _lastTreeData !== null &&
    state.data === _lastTreeData &&
    state.focusId === _lastTreeFocusId &&
    state.hideCompleted === _lastTreeHideCompleted &&
    currentHiddenTagIds === _lastTreeHiddenTagIds &&
    currentHighlightedTagIds === _lastTreeHighlightedTagIds &&
    state.selectedId !== _lastTreeSelectedId
  ) {
    // Deselect old
    if (_lastTreeSelectedId) {
      const oldNode = treeEl.querySelector(`.tree-node[data-id="${CSS.escape(_lastTreeSelectedId)}"]`);
      if (oldNode) oldNode.classList.remove("selected");
    }
    // Select new
    if (state.selectedId) {
      const newNode = treeEl.querySelector(`.tree-node[data-id="${CSS.escape(state.selectedId)}"]`);
      if (newNode) newNode.classList.add("selected");
    }
    _lastTreeSelectedId = state.selectedId;
    return;
  }

  // Fast path: nothing relevant changed — skip entirely
  if (
    state.data === _lastTreeData &&
    state.focusId === _lastTreeFocusId &&
    state.selectedId === _lastTreeSelectedId &&
    state.hideCompleted === _lastTreeHideCompleted &&
    currentHiddenTagIds === _lastTreeHiddenTagIds &&
    currentHighlightedTagIds === _lastTreeHighlightedTagIds
  ) {
    return;
  }

  // Many rows may change at once: a new focus or filter, or nothing drawn yet.
  const massChange = !_treeRows.size
    || state.focusId !== _lastTreeFocusId
    || state.hideCompleted !== _lastTreeHideCompleted
    || currentHiddenTagIds !== _lastTreeHiddenTagIds
    || currentHighlightedTagIds !== _lastTreeHighlightedTagIds;

  // Full rebuild needed — update cache
  _lastTreeData = state.data;
  _lastTreeFocusId = state.focusId;
  _lastTreeSelectedId = state.selectedId;
  _lastTreeHideCompleted = state.hideCompleted;
  _lastTreeHiddenTagIds = currentHiddenTagIds;
  _lastTreeHighlightedTagIds = currentHighlightedTagIds;

  // Handle root specially - it's the data object itself, not a child
  // rootId is dynamic (not always "root"), e.g., shared docs have different root IDs
  const rootId = state.data?.id;
  const focused =
    state.focusId === rootId ? state.data : cb.Store.find(state.focusId);

  if (!focused) {
    console.log("No focused node found");
    treeEl.innerHTML = `<div class="tree-empty">${t('views.tree.noData')}</div>`;
    _treeRows = new Map();
    return;
  }

  // Hide from accessibility tree during DOM manipulation to avoid Qt WebEngine
  // crashes (Falkon, ticking "hide completed"). Only when many rows may
  // change: hiding restyles every row (about 0.25 s at 20,000 items), more
  // than an edit's few rows cost.
  if (massChange) {
    treeEl.setAttribute("aria-hidden", "true");
    treeEl.style.visibility = "hidden";
  }

  const hiddenTagIds = cb.sharedCallbacks?.getHiddenTagIds?.() || [];
  const highlightedTagIds = cb.sharedCallbacks?.getHighlightedTagIds?.() || [];
  const doneTagId = cb.sharedCallbacks?.getDoneTagId?.() || null;
  const nodeIsDoneFn = cb.sharedCallbacks?.nodeIsDone || null;
  const tagFilter = cb.sharedCallbacks?.makeTagFilter?.();

  // Tag spotlight: toggle on container so non-matching rows fade via CSS.
  treeEl.classList.toggle('tag-filtering', highlightedTagIds.length > 0);

  // Ancestor-aware visibility predicate — matches mindmap/bubble: a self-hidden
  // node stays visible if any descendant survives, so the path to a matching
  // leaf stays reachable. Done-cascade is exclusive: hiding Done drops the
  // whole Done subtree regardless of descendant matches.
  const hide = !!state.hideCompleted;
  const doneHidden = hide || (!!doneTagId && hiddenTagIds.includes(doneTagId));
  const isDone = (n) => n.status === "done" || !!nodeIsDoneFn?.(n);
  const isSelfHidden = (n) => {
    if (hide && isDone(n)) return true;
    if (tagFilter?.isHidden(n, hiddenTagIds)) return true;
    return false;
  };
  const visibilityCache = new Map();
  const isVisible = (n) => {
    if (visibilityCache.has(n.id)) return visibilityCache.get(n.id);
    let v;
    if (doneHidden && isDone(n)) v = false;
    else if (!isSelfHidden(n)) v = true;
    else v = (n.children || []).some(isVisible);
    visibilityCache.set(n.id, v);
    return v;
  };

  // One bottom-up pass for all descendant counts. The previous per-node
  // recursive countDesc() summed to O(n·depth) across a full render.
  const descCounts = new Map();
  if (state.data) {
    (function countAll(n) {
      let total = 0;
      for (const c of n.children || []) {
        total += 1 + countAll(c);
      }
      descCounts.set(n.id, total);
      return total;
    })(state.data);
  }

  // Precompute ancestors of highlighted nodes — they get .tag-match-ancestor
  // so the dim overlay doesn't block the visual path down to a match.
  const tagMatchAncestorIds = new Set();
  if (highlightedTagIds.length) {
    const isMatch = (n) => !!tagFilter?.isMatch(n, highlightedTagIds);
    const visit = (n) => {
      let any = isMatch(n);
      for (const c of n.children || []) {
        if (visit(c)) any = true;
      }
      if (any && !isMatch(n)) tagMatchAncestorIds.add(n.id);
      return any;
    };
    visit(state.data);
  }

  const readOnly = cb.Store.isReadOnly();
  const pass = ++_treeRowsPass;

  // The clickable part of a row: dot, name, routine badge, collapse toggle,
  // descendant count.
  function buildRow(node, level, status, count, match, hasChildren) {
    const nodeDiv = document.createElement("div");
    nodeDiv.className = "tree-node";
    if (node.id === state.selectedId) {
      nodeDiv.classList.add("selected");
    }
    nodeDiv.dataset.id = node.id;
    nodeDiv.dataset.depth = level;
    if (status) {
      nodeDiv.dataset.status = status;
    }

    // Tag spotlight: mark matching nodes (and their ancestors-to-a-match) so
    // CSS dims everything else when .tag-filtering is on.
    if (match === "m") nodeDiv.classList.add("tag-match");
    else if (match === "a") nodeDiv.classList.add("tag-match-ancestor");

    // Drag handle (only in edit mode, uses pointer events not HTML5 DnD)
    if (!readOnly) {
      const handle = document.createElement('span');
      handle.className = 'tree-drag-handle';
      handle.textContent = '\u22EE\u22EE';
      nodeDiv.appendChild(handle);
    }

    // Color dot
    const dot = document.createElement("div");
    dot.className = "tree-dot";
    dot.style.background = node.color || "#6b7280";

    // Node name
    const nameSpan = document.createElement("span");
    nameSpan.className = "tree-name";
    nameSpan.textContent = node.name;

    // Collapse toggle (right of name, shows on hover, always visible when
    // collapsed). Click handling is delegated on #tree — see _attachTreeListeners.
    const toggleBtn = (hasChildren && !readOnly)
      ? (() => {
          const btn = document.createElement("span");
          btn.className = "tree-collapse-toggle";
          btn.textContent = node.collapsed ? "+" : "−";
          btn.title = node.collapsed ? t('views.expand') : t('views.collapse');
          return btn;
        })()
      : null;

    // Descendant count
    const countSpan =
      count > 0
        ? (() => {
            const span = document.createElement("span");
            span.className = "tree-count";
            span.textContent = count;
            return span;
          })()
        : null;

    // Assemble node: dot, name, toggle, [spacer], count
    nodeDiv.appendChild(dot);
    nodeDiv.appendChild(nameSpan);
    appendRoutineBadge(nodeDiv,node.id);
    if (toggleBtn) nodeDiv.appendChild(toggleBtn);
    const flexSpacer = document.createElement('span');
    flexSpacer.style.flex = '1';
    nodeDiv.appendChild(flexSpacer);
    if (countSpan) nodeDiv.appendChild(countSpan);

    // Click-to-select / dblclick-to-focus are delegated on #tree —
    // see _attachTreeListeners. Per-node listeners cost 2 closures per row
    // on every full render (drag already delegates the same way).
    return nodeDiv;
  }

  // A node's .tree-item, its row redrawn only when what it shows changed
  // (routine badges are updated in place by interfaces/classic/ui/routines.js; the selection is
  // set below), its children placed in turn.
  function drawItem(node, level) {
    const hasChildren = !!node.children?.length;
    const isCollapsed = hasChildren && !!node.collapsed;
    const count = descCounts.get(node.id) ?? 0;
    const status = cb.sharedCallbacks?.getEffectiveStatus?.(node) || node.status;
    const match = !highlightedTagIds.length ? ""
      : tagFilter?.isMatch(node, highlightedTagIds) ? "m"
      : tagMatchAncestorIds.has(node.id) ? "a" : "";
    const sig = JSON.stringify([level, node.name, node.color, status, count, isCollapsed, hasChildren, readOnly, match]);

    let entry = _treeRows.get(node.id);
    // An id shown twice (never expected): the second one gets its own rows.
    const shared = entry?.pass === pass;
    if (!entry || shared) {
      const item = document.createElement("div");
      item.className = "tree-item";
      entry = { item, row: null, children: null, sig: null, pass };
      if (!shared) _treeRows.set(node.id, entry);
    }
    entry.pass = pass;
    if (entry.sig !== sig) {
      const row = buildRow(node, level, status, count, match, hasChildren);
      if (entry.row) entry.item.replaceChild(row, entry.row);
      else entry.item.prepend(row);
      entry.row = row;
      entry.sig = sig;
      entry.item.classList.toggle("collapsed", isCollapsed);
    }

    if (hasChildren && !isCollapsed) {
      if (!entry.children) {
        entry.children = document.createElement("div");
        entry.children.className = "tree-children";
        entry.item.appendChild(entry.children);
      }
      placeItems(entry.children, node.children, level + 1);
    } else if (entry.children) {
      entry.children.remove();
      entry.children = null;
    }
    return entry.item;
  }

  // Make `container` hold the items of the visible `nodes`, in order: items
  // already in place stay, others are moved or added, the rest removed.
  function placeItems(container, nodes, level) {
    const visible = (nodes || []).filter(isVisible);
    const wanted = new Set(visible.map((n) => n.id));
    const idOf = (el) => (el.firstChild?.classList?.contains("tree-node") ? el.firstChild.dataset.id : null);
    let cursor = container.firstChild;
    for (const node of visible) {
      while (cursor && !wanted.has(idOf(cursor))) {
        const next = cursor.nextSibling;
        cursor.remove();
        cursor = next;
      }
      const item = drawItem(node, level);
      if (cursor === item) cursor = cursor.nextSibling;
      else container.insertBefore(item, cursor);
    }
    while (cursor) {
      const next = cursor.nextSibling;
      cursor.remove();
      cursor = next;
    }
  }

  // Check if focused on actual root (rootId declared above)
  if (state.focusId === rootId && state.data) {
    placeItems(treeEl, state.data.children, 0);
  } else {
    placeItems(treeEl, focused.children, 0);
  }
  // Rows not shown any more (deleted, under a collapsed node, filtered out).
  for (const [id, entry] of _treeRows) if (entry.pass !== pass) _treeRows.delete(id);
  // The selection: kept rows may still carry the previous one.
  for (const el of treeEl.querySelectorAll(".tree-node.selected")) {
    if (el.dataset.id !== state.selectedId) el.classList.remove("selected");
  }
  if (state.selectedId) _treeRows.get(state.selectedId)?.row.classList.add("selected");

  if (massChange) {
    // Force layout calculation before restoring visibility
    // This ensures the browser has the complete tree layout ready,
    // so restoring visibility paints the finished state in one frame
    treeEl.offsetHeight; // eslint-disable-line no-unused-expressions

    // Restore visibility after DOM manipulation
    treeEl.style.visibility = "";
    treeEl.removeAttribute("aria-hidden");
  }
}

/**
 * Get the views map (for external access to view modules/containers).
 */
export function getViews() {
  return cb.views;
}
